/* eslint-disable sonarjs/cognitive-complexity */
import { useMsal } from "@azure/msal-react";
import { ChangeEvent, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import IModalProps from "../../../model/ModalProps";
import { axiosGetRequest, axiosPostRequest } from "../../../api/ApiClient";
import AutocompleteFloatLabel from "../../../components/AutocompleteFloatLabel";
import DialogMessage from "../../../components/DialogMessage";
import InputLabelTextArea from "../../../components/InputLabelTextArea";
import InputText from "../../../components/InputText";
import HolidayDTO from "../../../model/dto/HolidayDTO";
import TypeHolidayEnum from "../../../model/Enums/TypeHolidayEnum";
import HolidayParams from "../../../model/HolidayParams";
import SelectOption from "../../../model/SelectOption";
import ButtonStyles from "../../../styles/components/ButtonStyles";
import DialogStyles from "../../../styles/components/DialogStyles";
import GenericUtils from "../../../utils/GenericUtils";
import StringUtils from "../../../utils/types/StringUtils";

const { BtnPrimary, DefaultButtonLink } = ButtonStyles;
const { StyledContainerButton } = DialogStyles;

interface IModalSaveHolidayProps extends IModalProps {
    params?: HolidayParams;
    setOpenDialogQuestion: (value: boolean) => void;
    onSuccess: (e: any) => void;
}

function ModalFeriadoContent(props: IModalSaveHolidayProps) {
    const { instance } = useMsal();
    const acessToken = GenericUtils.getAccessTokenDocente();

    const translate = {
        operationFailure: StringUtils.translate("operationFailure"),
        operationSuccess: StringUtils.translate("operationSuccess"),
        titleInclude: StringUtils.translate("e6ff_TitleInclude"),
        titleEdit: StringUtils.translate("e6ff_TitleEdit"),
        labelType: StringUtils.translate("e6ff_LabelType"),
        labelName: StringUtils.translate("e6ff_LabelName"),
        labelCampus: StringUtils.translate("e6ff_LabelCampus"),
        labelDescription: StringUtils.translate("e6ff_LabelDescription"),
        btnSubmit: StringUtils.translate("e6ff_BtnSubmit"),
        btnCancel: StringUtils.translate("e6ff_BtnCancel"),
        dialogSuccessTitle: StringUtils.translate("e6ff_DialogSuccessTitle"),
        dialogQuestionTitle: StringUtils.translate("e6ff_DialogQuestionTitle"),
        dialogQuestionSubtitle: StringUtils.translate(
            "e6ff_DialogQuestionSubtitle"
        ),
        dialogQuestionBtnConfirm: StringUtils.translate(
            "e6ff_DialogQuestionBtnConfirm"
        ),
        dialogQuestionBtnCancel: StringUtils.translate(
            "e6ff_DialogQuestionBtnCancel"
        ),
    };

    // Default
    const [campusAutoCompleteList, setCampusAutoCompleteList] = useState<
        SelectOption[]
    >([]);
    const [typeHolidayAutoCompleteList, setTypeHolidayAutoCompleteList] =
        useState<SelectOption[]>([]);

    // Form
    const [type, setType] = useState<any[]>([]);
    const [name, setName] = useState<string>("");
    const [campus, setCampus] = useState<any[]>([]);
    const [description, setDescription] = useState<string>("");
    // DialogErrorMessage
    const [errorMessage, setErrorMessage] = useState("");

    const typeRecessSelected = type?.[0]?.value === "R";
    const typeNationalSelected = type?.[0]?.value === "N";

    const defaultStatesValid = Boolean(type.length) && Boolean(name.trim());
    const descriptionValid =
        (typeRecessSelected && Boolean(description.trim())) ||
        !typeRecessSelected;
    const campusValid =
        (!typeNationalSelected && Boolean(campus.length)) ||
        typeNationalSelected;
    const isFormValid = defaultStatesValid && descriptionValid && campusValid;

    const createHoliday = (dto: any) => {
        return axiosPostRequest(
            "/docente/v1/feriados",
            dto,
            acessToken,
            instance
        );
    };

    const handleSubmit = async () => {
        if (isFormValid) {
            const dto: HolidayDTO = {
                calendarioFeriadoId: props.params?.id,
                dataFeriado: props.params?.start,
                tipoFeriado: type?.[0]?.value,
                nomeFeriado: name,
                motivoRecesso: description,
                campus: campus?.[0]?.value,
            };
            if (!typeRecessSelected) dto.motivoRecesso = "";
            if (typeNationalSelected) {
                dto.motivoRecesso = "";
                dto.campus = "";
            }

            const response = await createHoliday(dto);

            if (GenericUtils.isSuccessStatus(response.status)) {
                props.onSuccess(
                    props?.params?.id
                        ? translate.operationSuccess
                        : translate.dialogSuccessTitle
                );
            } else {
                setErrorMessage(
                    response.data?.details || translate.operationFailure
                );
            }
        }
    };

    const getCampus = async () => {
        const newCampus: SelectOption[] = [];

        const response = await axiosGetRequest(
            `/docente/v1/centros-resultado/campi`,
            acessToken,
            instance
        );

        response.data.map((data: any) =>
            newCampus.push({
                value: data,
                label: data,
            })
        );

        setCampusAutoCompleteList(newCampus);
    };

    useEffect(() => {
        if (props.open) {
            getCampus();
            setTypeHolidayAutoCompleteList(TypeHolidayEnum.getAll);
            if (props.params?.id) {
                if (props?.params?.title) setName(props?.params?.title);
                if (props?.params?.description)
                    setDescription(props?.params?.description);
                if (props?.params?.type)
                    setType([TypeHolidayEnum.getByValue(props?.params?.type)]);
                setCampus([
                    {
                        label: props?.params?.campus,
                        value: props?.params?.campus,
                    },
                ]);
            }
        } else {
            setType([]);
            setName("");
            setCampus([]);
            setDescription("");
        }
    }, []);

    useEffect(() => {
        if (typeRecessSelected) {
            setName("Recesso");
        }
        if (typeNationalSelected) {
            setCampus([]);
        }
    }, [typeRecessSelected, typeNationalSelected]);

    const handleChangeName = (event: ChangeEvent<{ value: any }>) => {
        const setValue = event.target.value as string;
        const shouldSetValue = setValue.length < 101;
        if (shouldSetValue) setName(event.target.value as string);
    };

    const handleCloseDialogError = () => {
        setErrorMessage("");
    };

    const DialogError = (
        <DialogMessage
            type="error"
            show={Boolean(errorMessage)}
            title={errorMessage}
            onClose={handleCloseDialogError}
        />
    );

    return (
        <>
            {props.params?.id && (
                <Row>
                    <Col
                        xs="2"
                        style={{
                            display: "flex",
                            justifyContent: "space-around",
                            alignItems: "center",
                            marginBottom: "15px",
                        }}
                    >
                        <div
                            style={{
                                width: "16px",
                                height: "16px",
                                background: props.params?.color,
                                borderRadius: "2px",
                            }}
                        />
                    </Col>
                    <Col
                        xs="10"
                        style={{
                            textAlign: "initial",
                            fontWeight: "700",
                            fontSize: "18px",
                        }}
                    >
                        <Row className="mb-3">
                            <Col>{props.params?.title}</Col>
                        </Row>
                    </Col>
                </Row>
            )}
            <Row>
                <Col>
                    <AutocompleteFloatLabel
                        label={translate.labelType}
                        options={typeHolidayAutoCompleteList}
                        selected={type}
                        onChange={setType}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <InputText
                        label={translate.labelName}
                        floatLabel
                        value={name}
                        onChange={handleChangeName}
                        disabled={typeRecessSelected}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <AutocompleteFloatLabel
                        label={translate.labelCampus}
                        options={campusAutoCompleteList}
                        selected={campus}
                        onChange={setCampus}
                        disabled={typeNationalSelected}
                    />
                </Col>
            </Row>
            {typeRecessSelected && (
                <Row>
                    <Col>
                        <InputLabelTextArea
                            label={translate.labelDescription}
                            maxLength={200}
                            value={description}
                            onChange={(event: any) =>
                                setDescription(event.target.value as string)
                            }
                            style={{
                                height: "107px",
                                paddingTop: "25px",
                            }}
                        />
                    </Col>
                </Row>
            )}
            <StyledContainerButton>
                <BtnPrimary disabled={!isFormValid} onClick={handleSubmit}>
                    {translate.btnSubmit}
                </BtnPrimary>
            </StyledContainerButton>
            <StyledContainerButton>
                <DefaultButtonLink
                    onClick={() => props.setOpenDialogQuestion(true)}
                >
                    {translate.btnCancel}
                </DefaultButtonLink>
            </StyledContainerButton>
            {errorMessage ? DialogError : null}
        </>
    );
}

export default ModalFeriadoContent;
